import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import { formatEmailListDate, formatTimelineDate } from 'services/utils/dateTimeUtils';
import { ListColumnType, ListFilterFieldType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import SecondaryButton from 'components/buttons/SecondaryButton';
import DeleteModal from 'components/modals/DeleteModal';
import { renderNewsletterStatusBadge, renderRecipientStatusBadge } from './NewsletterStatusBadges';
import { Newsletter, NEWSLETTER_PATH } from './NewsletterTypes';
import '../../../style/scss/detailpage.scss';
import '../../../style/scss/knowledge-base.scss';
import '../../../style/scss/newsletters.scss';

// The interval in milliseconds the report refreshes while the newsletter is sending
const SENDING_POLL_INTERVAL = 5000;

// The recipient statuses the report can filter on
const RECIPIENT_STATUS_OPTIONS = ['queued', 'sent', 'delivered', 'opened', 'clicked', 'failed', 'unsubscribed', 'complained'];

// Formats a share as a percentage of a total
const percentage = (value: number, total: number): string => (total ? `${Math.round((value / total) * 100)}%` : '0%');

// The report page of a sent newsletter: the counters and the recipients with their status
const NewsletterReportPage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone } = useSettings();
    const refetchListRef = useRef<() => void>(() => {});

    // Fetch the newsletter
    const { response, refetch } = useFetchData({ apiUrl: `get_newsletter/${id}` });
    const newsletter = response as Newsletter | null;

    // Refresh the counters and the recipients while the newsletter is sending
    useEffect(() => {
        if (newsletter?.status !== 'sending') return;
        const timer = setInterval(() => {
            refetch();
            refetchListRef.current();
        }, SENDING_POLL_INTERVAL);
        return () => clearInterval(timer);
    }, [newsletter?.status]);

    // A concept opens in the editor instead
    useEffect(() => {
        if (newsletter?.status === 'concept') history.replace(`${NEWSLETTER_PATH}/${newsletter.id}`);
    }, [newsletter?.status]);

    // Opens the delete confirmation of the newsletter
    const handleDelete = () => {
        initializeModal(
            <DeleteModal onConfirmDelete={async () => {
                try {
                    await saveData({ apiUrl: `patch_newsletter/${id}`, method: 'patch', data: { deleted: true } });
                    history.push(NEWSLETTER_PATH);
                } catch (error) {
                    setFloatingAlert({ type: 'danger', message: 'newsletters.general.save_failed_message' });
                }
            }} />,
            { modalSize: 'extra-small' }
        );
    };

    // Formats a timestamp of a recipient
    const formatStamp = (value: string | null) => (value ? formatEmailListDate(value, userLocale, userTimezone) : '');

    // The columns of the recipient list
    const columns: ListColumnType[] = useMemo(() => [
        { width: '1.5fr', field: 'name', label: 'newsletters.report.column_name' },
        { width: '2fr', field: 'email', label: 'newsletters.report.column_email' },
        {
            width: '1.25fr', field: 'status', label: 'newsletters.report.column_status',
            render: (value: any) => renderRecipientStatusBadge(value),
        },
        { width: '1fr', field: 'delivered_at', label: 'newsletters.report.column_delivered', render: (value: string | null) => <span>{formatStamp(value)}</span> },
        {
            width: '1fr', field: 'opened_at', label: 'newsletters.report.column_opened',
            render: (value: string | null, item: any) => <span>{formatStamp(value)}{item.open_count > 1 ? ` (${item.open_count}×)` : ''}</span>,
        },
        {
            width: '1fr', field: 'clicked_at', label: 'newsletters.report.column_clicked',
            render: (value: string | null, item: any) => <span>{formatStamp(value)}{item.click_count > 1 ? ` (${item.click_count}×)` : ''}</span>,
        },
        {
            width: '1.5fr', field: 'error_message', label: 'newsletters.report.column_error', disableOrdering: true,
            render: (value: string | null) => <span className='newsletter-recipient-error' title={value ?? ''}>{value ?? ''}</span>,
        },
    ], [t, userLocale, userTimezone]);

    // The filters of the recipient list
    const filterFields: ListFilterFieldType[] = useMemo(() => [
        { type: 'search', name: 'search', placeholder: 'newsletters.report.search_placeholder', queryParam: 'search' },
        {
            type: 'dropdown', name: 'status', label: 'newsletters.report.column_status', queryParam: 'status',
            backendField: 'id', optionField: 'id', translationPrefix: 'newsletters.recipient_status.',
            hardcodedOptions: RECIPIENT_STATUS_OPTIONS.map(status => ({ id: status })),
            width: 1.25,
        },
    ], []);

    // The counters shown as tiles
    const tiles = newsletter ? [
        { key: 'recipients', value: newsletter.recipient_count, sub: null },
        { key: 'sent', value: newsletter.sent_count, sub: percentage(newsletter.sent_count, newsletter.recipient_count) },
        { key: 'delivered', value: newsletter.delivered_count, sub: percentage(newsletter.delivered_count, newsletter.sent_count) },
        { key: 'opened', value: newsletter.opened_count, sub: percentage(newsletter.opened_count, newsletter.delivered_count) },
        { key: 'clicked', value: newsletter.clicked_count, sub: percentage(newsletter.clicked_count, newsletter.delivered_count) },
        { key: 'failed', value: newsletter.failed_count, sub: percentage(newsletter.failed_count, newsletter.recipient_count) },
        { key: 'unsubscribed', value: newsletter.unsubscribed_count, sub: percentage(newsletter.unsubscribed_count, newsletter.delivered_count) },
    ] : [];

    // Show the loader until the newsletter is fetched
    if (!newsletter) {
        return (
            <div className='detailpage container-filled'>
                <div className='inline-form-loader'>
                    <div className='loader'></div>
                </div>
            </div>
        );
    }

    // Describes the sender line below the title
    const senderLine = newsletter.from_email
        ? (newsletter.from_name ? `${newsletter.from_name} <${newsletter.from_email}>` : newsletter.from_email)
        : '';

    return (
        <div className='detailpage newsletter-report-page container-filled'>
            <div className='detailpage-header row'>
                <div className='header-title'>
                    <span className='back-button' onClick={() => history.push(NEWSLETTER_PATH)}>
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </span>
                    <h2 className='fixed-title'>{newsletter.subject || t('newsletters.general.untitled')}</h2>
                    <div className='newsletter-report-status'>{renderNewsletterStatusBadge(newsletter.status)}</div>
                </div>
                <div className='header-buttons'>
                    {newsletter.status !== 'sending' && (
                        <SecondaryButton label='general.delete' size='small' icon={<FontAwesomeIcon icon={faTrash} />} onClick={handleDelete} />
                    )}
                </div>
            </div>

            <div className='kb-editor-page newsletter-report-sheet-wrapper'>
                <div className='kb-editor-sheet newsletter-report-sheet'>
                    <div className='newsletter-report-meta'>
                        <p className='explanation-text'>
                            {t('newsletters.report.sent_line', {
                                date: newsletter.sent_at ? formatTimelineDate(newsletter.sent_at, false) : '',
                                name: newsletter.sent_by?.name ?? '',
                            })}
                            {senderLine && ` · ${senderLine}`}
                        </p>
                        {newsletter.status === 'sending' && <p className='newsletter-report-sending'>{t('newsletters.report.sending_notice')}</p>}
                        {newsletter.error_message && <p className='error-text'>{newsletter.error_message}</p>}
                    </div>

                    <div className='newsletter-report-tiles'>
                        {tiles.map(tile => (
                            <div key={tile.key} className={`newsletter-report-tile newsletter-report-tile-${tile.key}`}>
                                <span className='newsletter-report-tile-value'>{tile.value}</span>
                                <span className='newsletter-report-tile-label'>{t(`newsletters.report.tile_${tile.key}`)}</span>
                                {tile.sub && <span className='newsletter-report-tile-sub'>{tile.sub}</span>}
                            </div>
                        ))}
                    </div>

                    <div className='newsletter-report-list'>
                        <AbstractList
                            query={{ endpoint: `get_newsletter_recipient_list/${id}`, object: 'newsletter_recipient' }}
                            objectName='newsletters.report.recipient_object_name'
                            headerTitle='newsletters.report.recipients_title'
                            columns={columns}
                            filterFields={filterFields}
                            isPaginated={true}
                            showAddButton={false}
                            showSearch={false}
                            setRefetchList={(refetchList) => { refetchListRef.current = refetchList; }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsletterReportPage;
